import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import WPDropdown from "../../packages/WPDropdown";
import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";

import { ensureChemicalLibraryHydrated } from "../../components/chemicalLibrary/ChemicalLibraryDialog/ChemicalLibraryDialog.api";
import { getChemicalOptionsByCategory } from "../../components/ChemicalSelectionFields/utils";
import {
  CIP_SECTION_META,
  DEFAULT_CIP_INFORMATION,
  getCIPPriceRanges,
} from "./defaultCIPInformationFields.config";

import {
  CURRENCY_SYMBOLS,
  DEFAULT_CURRENCY_SYMBOL,
} from "../../constants/currencies.constants";
import {
  ACID_KEY,
  ADDITIVE_ACID_KEY,
  ADDITIVE_ALKALI_KEY,
  ALKALI_KEY,
  CATEGORY_TO_CHEM_ID_KEY,
} from "../../constants/global.constants";

import "./CIPSequenceChemicalSection.scss";

const formatPriceValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const CIPSequenceChemicalSection = ({
  defaultCIPKeys,
  errors,
  handleInputChange,
  onFieldChange,
  onMandatoryValidationChange,
  onSectionValidationToggle,
  disabled,
  existingCIPState,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    temperature,
    mass,
    currency,
    conversion_rate,
    plantChemicalLibrary,
    userChemicalLibrary,
  } = useSelector((state) => state);

  const defaultCIPState = useSelector(
    (state) => ({
      acid_chem_id: state.acid_chem_id,
      alkali_chem_id: state.alkali_chem_id,
      additive_alkali_chem_id: state.additive_alkali_chem_id,
      additive_acid_chem_id: state.additive_acid_chem_id,
      acid_price: state.acid_price,
      alkali_price: state.alkali_price,
      additive_alkali_price: state.additive_alkali_price,
      additive_acid_price: state.additive_acid_price,
      acid_used_per_cip: state.acid_used_per_cip,
      alkali_used_per_cip: state.alkali_used_per_cip,
      additive_alkali_used_per_cip: state.additive_alkali_used_per_cip,
      additive_acid_used_per_cip: state.additive_acid_used_per_cip,
      acid_cip_ph: state.acid_cip_ph,
      alkali_cip_ph: state.alkali_cip_ph,
      additive_alkali_ph: state.additive_alkali_ph,
      additive_acid_ph: state.additive_acid_ph,
      acid_cip_temp: state.acid_cip_temp,
      alkali_cip_temp: state.alkali_cip_temp,
      additive_alkali_temp: state.additive_alkali_temp,
      additive_acid_temp: state.additive_acid_temp,
      acid_cip_duration: state.acid_cip_duration,
      alkali_cip_duration: state.alkali_cip_duration,
      additive_alkali_duration: state.additive_alkali_duration,
      additive_acid_duration: state.additive_acid_duration,
    }),
    shallowEqual,
  );

  const normalizedCIPKeys = useMemo(() => {
    if (!Array.isArray(defaultCIPKeys)) {
      return [];
    }

    return defaultCIPKeys.filter(
      (key) => typeof key === "string" && key.trim() && CIP_SECTION_META[key],
    );
  }, [defaultCIPKeys]);

  const normalizedCIPKeySignature = useMemo(
    () => normalizedCIPKeys.join("|"),
    [normalizedCIPKeys],
  );

  const chemicalLibrary = useMemo(() => {
    if (plantChemicalLibrary?.length) {
      return plantChemicalLibrary;
    }

    return userChemicalLibrary || [];
  }, [plantChemicalLibrary, userChemicalLibrary]);

  const chemicalOptionsByCategory = useMemo(() => {
    const optionsByChemicalField =
      getChemicalOptionsByCategory(chemicalLibrary);

    return {
      acid: optionsByChemicalField?.acid || [],
      alkali: optionsByChemicalField?.alkali || [],
      additive_alkali: optionsByChemicalField?.additive_alkali || [],
      additive_acid: optionsByChemicalField?.additive_alkali || [],
    };
  }, [chemicalLibrary]);

  useEffect(() => {
    if (chemicalLibrary?.length) {
      return;
    }

    const fetchData = async () => {
      await ensureChemicalLibraryHydrated(dispatch, t);
    };

    fetchData();
  }, [chemicalLibrary, dispatch, t]);

  const getDefaultChemicalOption = (category) => {
    const options = chemicalOptionsByCategory?.[category] || [];

    if (!options.length) {
      return null;
    }

    const chemIdKey = CATEGORY_TO_CHEM_ID_KEY[category];
    const defaultChemicalId =
      existingCIPState?.[chemIdKey] ?? defaultCIPState?.[chemIdKey];

    const displayNameByChemId = chemicalLibrary.find(
      (chem) => Number(chem.chem_id) === Number(defaultChemicalId),
    )?.display_name;

    return (
      options.find((option) => option.value === displayNameByChemId) || null
    );
  };

  const getSelectedChemicalsState = () => ({
    [ACID_KEY]: getDefaultChemicalOption(ACID_KEY),
    [ALKALI_KEY]: getDefaultChemicalOption(ALKALI_KEY),
    [ADDITIVE_ALKALI_KEY]: getDefaultChemicalOption(ADDITIVE_ALKALI_KEY),
    [ADDITIVE_ACID_KEY]: getDefaultChemicalOption(ADDITIVE_ACID_KEY),
  });

  const [selectedChemicals, setSelectedChemicals] = useState(
    getSelectedChemicalsState,
  );

  useEffect(() => {
    setSelectedChemicals(getSelectedChemicalsState());
  }, [chemicalOptionsByCategory, existingCIPState, defaultCIPState]);

  const getPriceValue = (category) => {
    const priceKey = CIP_SECTION_META[category]?.priceKey;
    if (!priceKey) {
      return "";
    }

    const explicitPrice =
      existingCIPState?.[priceKey] ?? defaultCIPState?.[priceKey];

    if (explicitPrice !== undefined && explicitPrice !== null) {
      return formatPriceValue(explicitPrice);
    }

    return "";
  };

  const getChemicalLabel = (category) => {
    const chemicalLabelKey = CIP_SECTION_META?.[category]?.chemicalLabelKey;
    if (chemicalLabelKey) {
      return chemicalLabelKey.includes(".")
        ? t(chemicalLabelKey)
        : t(`defaultInformationStrings.${chemicalLabelKey}`);
    }

    return "";
  };

  const getFieldLabel = (key) => {
    if (!key) {
      return "";
    }

    return key.includes(".") ? t(key) : t(`defaultInformationStrings.${key}`);
  };

  const defaultFields = useMemo(
    () => DEFAULT_CIP_INFORMATION(temperature, mass),
    [temperature, mass],
  );
  const activePriceRangeByCategory = useMemo(
    () => getCIPPriceRanges(currency, mass, conversion_rate, normalizedCIPKeys),
    [currency, mass, conversion_rate, normalizedCIPKeys],
  );

  const handleChemicalChange = (category, option) => {
    setSelectedChemicals((prev) => ({
      ...prev,
      [category]: option,
    }));

    const sectionMeta = CIP_SECTION_META[category] || {};
    if (onFieldChange && sectionMeta.chemicalKey) {
      const selectedChemicalId = chemicalLibrary.find(
        (chem) => chem.display_name === option?.value,
      )?.chem_id;

      onFieldChange(sectionMeta.chemicalKey, selectedChemicalId);
    }

    if (option?.price !== undefined && option?.price !== null) {
      const updatedPrice = formatPriceValue(option.price);
      const minValue = activePriceRangeByCategory?.[category]?.min;
      const maxValue = activePriceRangeByCategory?.[category]?.max;

      if (handleInputChange && sectionMeta.priceKey) {
        handleInputChange(
          sectionMeta.priceKey,
          updatedPrice,
          minValue,
          maxValue,
        );
      } else if (onFieldChange && sectionMeta.priceKey) {
        onFieldChange(sectionMeta.priceKey, updatedPrice);
      }
    }
  };

  const handlePriceChange = (category, value) => {
    const priceKey = CIP_SECTION_META[category]?.priceKey;
    const minValue = activePriceRangeByCategory?.[category]?.min;
    const maxValue = activePriceRangeByCategory?.[category]?.max;

    if (handleInputChange && priceKey) {
      handleInputChange(priceKey, value, minValue, maxValue);
    } else if (onFieldChange && priceKey) {
      onFieldChange(priceKey, value);
    }
  };

  const getValidationFieldsForCategory = (category) => {
    const sectionFields = Object.values(defaultFields?.[category] || {});
    const priceKey = CIP_SECTION_META[category]?.priceKey;

    if (!priceKey) {
      return sectionFields;
    }

    return [
      {
        key: priceKey,
        ranges: {
          hard: {
            min: activePriceRangeByCategory?.[category]?.min,
            max: activePriceRangeByCategory?.[category]?.max,
          },
        },
      },
      ...sectionFields,
    ];
  };

  const getFieldValue = (fieldKey) => {
    if (existingCIPState && existingCIPState[fieldKey] !== undefined) {
      return existingCIPState[fieldKey];
    }

    return defaultCIPState[fieldKey];
  };

  const processFieldSourceKey = normalizedCIPKeys[0];

  const [, sourcePhField, sourceTempField, sourceDurationField] = Object.values(
    defaultFields?.[processFieldSourceKey] || {},
  );

  const sharedProcessFields = [
    sourcePhField,
    sourceTempField,
    sourceDurationField,
  ].filter(Boolean);
  const isSingleLayout = normalizedCIPKeys.length === 1;
  const isCombinationLayout = normalizedCIPKeys.length > 1;

  const renderChemicalFields = (key) => {
    const selectedChemical = selectedChemicals?.[key] || null;
    const currencySymbol =
      CURRENCY_SYMBOLS[currency] || DEFAULT_CURRENCY_SYMBOL;
    const [usedPerCipField] = Object.values(defaultFields[key] || {});

    return (
      <>
        <div className="default-cip-v2-field">
          <WPDropdown
            id={`chemical_dropdown_${key}`}
            label={getChemicalLabel(key)}
            required={!disabled}
            value={selectedChemical}
            options={chemicalOptionsByCategory?.[key] || []}
            onChange={(option) => handleChemicalChange(key, option)}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => String(option.value)}
            placeholder={getChemicalLabel(key)}
            disabled={disabled}
            customClassName="default-cip-v2-chemical-dropdown"
          />
        </div>

        <div className="default-cip-v2-field">
          <WPRangedInput
            id={`chemical_price_${key}`}
            label={t("defaultInformationStrings.price")}
            required={!disabled}
            value={getPriceValue(key)}
            onChange={(e) => handlePriceChange(key, e.target.value)}
            ranges={{
              hard: {
                min: activePriceRangeByCategory?.[key]?.min,
                max: activePriceRangeByCategory?.[key]?.max,
              },
            }}
            fractions={3}
            maxDecimals={3}
            suffix={`${currencySymbol}/${mass}`}
            disabled={disabled}
            skipValidation={disabled}
            externalError={
              disabled ? null : errors?.[CIP_SECTION_META[key]?.priceKey]
            }
            showRangeAlways
            hideRangeAlways={disabled}
          />
        </div>

        {usedPerCipField && (
          <div className="default-cip-v2-field">
            <WPRangedInput
              value={getFieldValue(usedPerCipField.key)}
              name={usedPerCipField.key}
              required={!disabled}
              label={getFieldLabel(
                usedPerCipField.labelKey || usedPerCipField.key,
              )}
              suffix={usedPerCipField.unit}
              onChange={
                disabled
                  ? undefined
                  : (e) =>
                      handleInputChange(
                        usedPerCipField.key,
                        e.target.value,
                        usedPerCipField.ranges?.hard?.min,
                        usedPerCipField.ranges?.hard?.max,
                      )
              }
              ranges={usedPerCipField.ranges}
              disabled={disabled}
              skipValidation={disabled}
              externalError={disabled ? null : errors?.[usedPerCipField.key]}
              showRangeAlways
              hideRangeAlways={disabled}
            />
          </div>
        )}
      </>
    );
  };

  const renderRangedField = (field) => (
    <div key={field.key} className="default-cip-v2-field">
      <WPRangedInput
        value={getFieldValue(field.key)}
        name={field.key}
        required={!disabled}
        label={getFieldLabel(field.labelKey || field.key)}
        suffix={field.unit}
        onChange={
          disabled
            ? undefined
            : (e) =>
                handleInputChange(
                  field.key,
                  e.target.value,
                  field.ranges?.hard?.min,
                  field.ranges?.hard?.max,
                )
        }
        ranges={field.ranges}
        disabled={disabled}
        skipValidation={disabled}
        externalError={disabled ? null : errors?.[field.key]}
        showRangeAlways
        hideRangeAlways={disabled}
      />
    </div>
  );

  useEffect(() => {
    if (!onSectionValidationToggle || !normalizedCIPKeys.length) {
      return;
    }

    normalizedCIPKeys.forEach((category) => {
      onSectionValidationToggle(
        category,
        true,
        getValidationFieldsForCategory(category),
      );
    });
  }, [
    activePriceRangeByCategory,
    normalizedCIPKeySignature,
    existingCIPState,
    defaultCIPState,
    onSectionValidationToggle,
  ]);

  useEffect(() => {
    const hasMissingRequiredFields = normalizedCIPKeys.some((key) => {
      const selectedChemical = selectedChemicals?.[key];
      const priceValue = getPriceValue(key);

      return !selectedChemical?.label || priceValue === "";
    });

    if (onMandatoryValidationChange) {
      onMandatoryValidationChange(hasMissingRequiredFields);
    }
  }, [
    normalizedCIPKeySignature,
    existingCIPState,
    defaultCIPState,
    onMandatoryValidationChange,
    selectedChemicals,
  ]);

  if (!normalizedCIPKeys.length) {
    return null;
  }

  if (isSingleLayout) {
    const singleCategory = normalizedCIPKeys[0];

    return (
      <div className="default-cip-v2-grid default-cip-v2-grid--single-layout">
        <div className="default-cip-v2-row default-cip-v2-row--single-panel">
          <div className="default-cip-v2-fields default-cip-v2-fields--single">
            {renderChemicalFields(singleCategory)}
            {sharedProcessFields.map(renderRangedField)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="default-cip-v2-grid default-cip-v2-grid--split-layout">
      <div className="default-cip-v2-row default-cip-v2-row--left-panel">
        {normalizedCIPKeys.map((key, index) => {
          const rowClasses = [
            "default-cip-v2-fields",
            "default-cip-v2-fields--left",
          ];

          if (isCombinationLayout && index < normalizedCIPKeys.length - 1) {
            rowClasses.push("default-cip-v2-fields--with-divider");
          }

          return (
            <div key={key} className={rowClasses.join(" ")}>
              {renderChemicalFields(key)}
            </div>
          );
        })}
      </div>

      <div className="default-cip-v2-row default-cip-v2-row--right-panel">
        <div className="default-cip-v2-fields default-cip-v2-fields--right">
          {sharedProcessFields.map(renderRangedField)}
        </div>
      </div>
    </div>
  );
};

export default CIPSequenceChemicalSection;
